import './modules/selection-control/css/SelectionControl.css';
import pointsWithinPolygon from '@turf/points-within-polygon';
import * as atlas from 'azure-maps-control';
import * as drawing from 'azure-maps-drawing-tools';
import React, { useContext, useEffect, useRef } from 'react';
import { AzureMapsContext } from 'react-azure-maps';
import { SelectionControl, ShapeSelectionMode } from './modules/selection-control/control/SelectionControl';
import { Utils } from './modules/selection-control/helpers/Utils';

interface AzureMapSelectionDrawingControlProps {
    onSelectionChanged?: (selectedFeatures: atlas.data.Feature<atlas.data.Geometry, any>[]) => void;
    selectionSourceId?: string;
    shapeSelectionMode?: ShapeSelectionMode;
    selectionResetToken?: number;
    emitSelectionArea?: boolean;
}

export const AzureMapSelectionDrawingControl: React.FC<AzureMapSelectionDrawingControlProps> = ({
                                                                                                   onSelectionChanged,
                                                                                                   selectionSourceId,
                                                                                                   shapeSelectionMode = ShapeSelectionMode.any,
                                                                                                   selectionResetToken,
                                                                                                   emitSelectionArea = false,
                                                                                               }) => {
    const { mapRef, isMapReady } = useContext(AzureMapsContext);
    const drawingManagerRef = useRef<drawing.drawing.DrawingManager | null>(null);
    const selectionControlRef = useRef<SelectionControl | null>(null);
    const selectionChangedRef = useRef(onSelectionChanged);
    const selectionRequestRef = useRef<number | null>(null);

    useEffect(() => {
        selectionChangedRef.current = onSelectionChanged;
    }, [onSelectionChanged]);

    useEffect(() => {
        if (selectionResetToken === undefined) {
            return;
        }
        selectionControlRef.current?.clear();
    }, [selectionResetToken]);

    useEffect(() => {
        if (!isMapReady || !mapRef) return;

        if (selectionControlRef.current) {
            return;
        }

        // Initialize drawing manager without toolbar (SelectionControl provides the UI)
        const drawingManager = new drawing.drawing.DrawingManager(mapRef);
        drawingManagerRef.current = drawingManager;

        // Initialize selection control
        const selectionControl = new SelectionControl({
            drawingManager,
            shapeSelectionMode,
        });
        mapRef.controls.add(selectionControl, {
            position: atlas.ControlPosition.TopRight,
            zIndex: -1000,
        });
        selectionControlRef.current = selectionControl;

        const handleDrawingComplete = (e: any) => {
            const shape = e?.shape ?? e?.shapes?.[0] ?? e;
            const searchArea = Utils.getPolygon(shape);
            if (!searchArea) {
                return;
            }
            drawingManager.setOptions({ mode: drawing.drawing.DrawingMode.idle });
            if (emitSelectionArea) {
                const handler = selectionChangedRef.current;
                if (handler) {
                    handler([{
                        type: 'Feature',
                        geometry: toGeoJsonGeometry(searchArea),
                        properties: {},
                    } as any]);
                }
                return;
            }
            if (!mapRef || !selectionSourceId) {
                return;
            }
            const source = mapRef.sources.getById(selectionSourceId) as atlas.source.DataSource | atlas.source.VectorTileSource | undefined;
            if (!source || source instanceof atlas.source.VectorTileSource) {
                return;
            }
            const shapes = source.getShapes();
            if (!shapes || shapes.length === 0) {
                const handler = selectionChangedRef.current;
                if (handler) {
                    handler([]);
                }
                return;
            }
            if (selectionRequestRef.current !== null) {
                window.cancelAnimationFrame(selectionRequestRef.current);
            }
            selectionRequestRef.current = window.requestAnimationFrame(() => {
                const points: any[] = [];
                const refs: any[] = [];
                shapes.forEach((shape) => {
                    const feature = toGeoJsonFeature(shape);
                    const geometry = feature?.geometry;
                    if (!geometry || String(geometry.type).indexOf('Point') < 0) {
                        return;
                    }
                    const normalized = normalizeGeometry(geometry);
                    if (!normalized) {
                        return;
                    }
                    refs.push(shape);
                    points.push({
                        type: 'Feature',
                        geometry: normalized,
                        properties: { __shapeIndex: refs.length - 1 },
                    });
                });
                if (points.length === 0) {
                    const handler = selectionChangedRef.current;
                    if (handler) {
                        handler([]);
                    }
                    return;
                }
                const searchAreaFeature = {
                    type: 'Feature',
                    geometry: toGeoJsonGeometry(searchArea),
                    properties: {},
                } as any;
                const within = pointsWithinPolygon(
                    { type: 'FeatureCollection', features: points } as any,
                    searchAreaFeature as any,
                );
                const selected = (within?.features || []).map((f: any) => refs[f?.properties?.__shapeIndex]).filter(Boolean);
                const handler = selectionChangedRef.current;
                if (handler) {
                    handler(selected);
                }
            });
        };

        const handleSelectionChange = (selectedFeatures: any) => {
            const handler = selectionChangedRef.current;
            if (handler) {
                handler(selectedFeatures);
            }
        };

        // SelectionControl uses EventEmitter, but we need to know the event name.
        mapRef.events.add('drawingcomplete', drawingManager, handleDrawingComplete);
        selectionControl._addEventListener('dataselected', handleSelectionChange, true);

        return () => {
            mapRef.controls.remove(selectionControl);
            mapRef.events.remove('drawingcomplete', drawingManager, handleDrawingComplete);
            selectionControl._removeEventListener('dataselected', handleSelectionChange);
            if (drawingManagerRef.current) {
                drawingManagerRef.current.dispose();
            }
            if (selectionRequestRef.current !== null) {
                window.cancelAnimationFrame(selectionRequestRef.current);
                selectionRequestRef.current = null;
            }
            selectionControlRef.current = null;
        };
    }, [isMapReady, mapRef, selectionSourceId, shapeSelectionMode]);


    return null;
};

function toGeoJsonGeometry(geometry: any): any {
    if (!geometry) {
        return geometry;
    }
    if (geometry.type && geometry.coordinates) {
        return geometry;
    }
    if (typeof geometry.getType === 'function' && typeof geometry.getCoordinates === 'function') {
        return {
            type: geometry.getType(),
            coordinates: geometry.getCoordinates(),
        };
    }
    if (typeof geometry.toJson === 'function') {
        const json = geometry.toJson();
        if (json?.type && json?.coordinates) {
            return json;
        }
        if (json?.geometry?.type && json?.geometry?.coordinates) {
            return json.geometry;
        }
    }
    return geometry;
}

function normalizeGeometry(geometry: any): any {
    if (!geometry) {
        return geometry;
    }
    if (geometry.type && geometry.coordinates) {
        return geometry;
    }
    if (typeof geometry.getType === 'function' && typeof geometry.getCoordinates === 'function') {
        return {
            type: geometry.getType(),
            coordinates: geometry.getCoordinates(),
        };
    }
    if (geometry.coordinates) {
        return geometry;
    }
    return geometry;
}

function toGeoJsonFeature(feature: any): any {
    if (!feature) {
        return feature;
    }
    if (feature.type === 'Feature' && feature.geometry) {
        return {
            ...feature,
            geometry: normalizeGeometry(feature.geometry),
        };
    }
    if (typeof feature.toJson === 'function') {
        return feature.toJson();
    }
    if (feature instanceof atlas.Shape) {
        return feature.toJson();
    }
    return feature;
}
